import { useApp } from '@libs/hooks';
import { AssetsSvgMap, TItem, TWidgetProps } from '@libs/types';
import { ConditionalRender } from '@libs/utils';
import React from 'react';
import { TConsumerInputPhoneData, TConsumerListData } from '../../../types/data';
import { RowEnum } from '../../../types/enums';
import { TListOption } from '../../../types/options';
import { convertItemToRowComponentProps } from '../../../utils/helpers';
import { getInlineStyles } from '../../../utils/styles';
import { Separator } from '../../Separator/Separator';
import { Modal } from '../../UI/Modal/Modal';
import RowComponent from '../../UI/Row/RowComponent';
import InputSearch from '../InputSearch';
import ListItems, { TListItemProps } from '../ListItems';
import { SEARCH_ENABLED_ITEMS_COUNT_DEFAULT } from '../utils/ListConstant';
import ListSearch from './ListSearch';
import ListSectionItems from './ListSectionItems';
import styles from '../styles/styles.module.css';

export type TListSelectViewProps = {
    /**
     * The widget object used for data access and forwarding to sub-components.
     * No behavior or interaction logic is derived directly here.
     */
    widget: TWidgetProps<TConsumerListData & TConsumerInputPhoneData, TListOption>['widget'];
    /** Pre-resolved hidden state from useBehavior. */
    isHidden: boolean;
    /** Pre-resolved disabled state from useBehavior. */
    isDisabled: boolean;
    /** Row widget id for render item */
    rowId: RowEnum;
    /** Transformed list data with items enriched for selection state. */
    dataForListSelect: TConsumerListData & TConsumerInputPhoneData;
    // closeModal: () => void;
    // handleClick: (uid: string, item: TItem) => void;
    /** Optional override called instead of handleClick when an item is selected. */
    onSelectItem: (uid: string, item: TItem) => void;
    /** Callback to close or restore the modal depending on isOpenInModal. */
    isOpenInModal?: boolean;
};

/**
 * Composable view for the list-select widget.
 * Owns only rendering logic and delegates all behavior-derived state
 * (isHidden, isDisabled, renderItem, closeModal, onInputSearchHandleClick)
 * to the caller.
 *
 * Use this component when embedding a list-select view inside a larger
 * widget tree where behavior hooks are already managed one level up.
 */
const ListSelectView = ({ widget, isHidden, dataForListSelect, rowId, isOpenInModal, onSelectItem }: TListSelectViewProps) => {
    const searchEnabledItemsCount = widget.options?.searchEnabledItemsCount ?? SEARCH_ENABLED_ITEMS_COUNT_DEFAULT;
    const searchWidget = { ...widget, options: {} };
    const { setModal } = useApp();

    const renderItem: React.FC<TListItemProps> = ({ item, firstLetter, addLetter }) => {
        const newItem = convertItemToRowComponentProps(item, rowId);
        if (newItem) {
            return (
                <React.Fragment key={item.id}>
                    <ConditionalRender condition={addLetter}>
                        <div className={styles.firstLetterRow}>
                            <div className={styles.firstLetter}>{firstLetter}</div>
                        </div>
                    </ConditionalRender>
                    <RowComponent
                        {...newItem}
                        handleClick={() => {
                            if (!newItem.isDisabled) {
                                onSelectItem?.(widget.uid, item);
                            }
                        }}
                        id={rowId ?? RowEnum.ROW_REGULAR}
                    />
                </React.Fragment>
            );
        }
        return false;
    };

    const closeModal = () => {
        if (isOpenInModal) {
            setModal(
                <Modal isCanCloseModal={true} iconName={AssetsSvgMap.ic_arrow_back} onHandleClick={() => setModal(null)}>
                    <ListSelectView
                        widget={widget}
                        isHidden={false}
                        isDisabled={false}
                        dataForListSelect={dataForListSelect}
                        onSelectItem={onSelectItem}
                        rowId={rowId as RowEnum}
                    />
                </Modal>,
            );
        } else {
            setModal(null);
        }
    };

    const onInputSearchHandleClick = () => {
        setModal?.(
            <Modal isCanCloseModal={false} noTopPadding iconName={AssetsSvgMap.ic_arrow_back}>
                <ListSearch widget={widget} renderItem={renderItem} closeModal={closeModal} />
            </Modal>,
        );
    };

    if (!isHidden && Array.isArray(dataForListSelect.items) && dataForListSelect.items.length > 0) {
        const allItemsHeader = dataForListSelect?.allItemsHeader ?? dataForListSelect?.messages?.allItemsHeader;
        return (
            <div data-test={widget.uid} style={getInlineStyles({ margin: widget.options?.margin })}>
                <ListSectionItems data={dataForListSelect} renderItem={renderItem} />
                <ConditionalRender condition={!!allItemsHeader}>
                    <Separator isWidget={false} separatorVariant={'subheader'} text={allItemsHeader?.toLowerCase()} />
                </ConditionalRender>
                <ConditionalRender condition={dataForListSelect.items.length >= searchEnabledItemsCount}>
                    <InputSearch
                        widget={searchWidget}
                        asButton={true}
                        handleClick={onInputSearchHandleClick}
                        handleCloseClick={closeModal}
                        marginBottom={24}
                        marginLeft={24}
                        marginRight={24}
                    />
                </ConditionalRender>
                <ListItems items={dataForListSelect.items} renderItem={renderItem} sortByAlphabet={widget.options?.sortByAlphabet} />
            </div>
        );
    }
    return null;
};

export { ListSelectView };
